import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UserService} from '../../services/user.service';
import {DialogService} from '../../services/dialog.service';
import {NotifierService} from 'angular-notifier';
import {AuthorityUser, UserSetting} from '../../models/user-setting';
import {SharedValueService} from '../../services/shared-value.service';
import {DialogResult} from '../../models/dialog-param';
import {DatatableComponent} from '@swimlane/ngx-datatable';
import {Subscription} from 'rxjs/Rx';
import {Helper} from '../../common/helper';

class User {
  // full name
  public name: string;
  public email: string;
  public password: string;
  public group_id: number;

  constructor() {
    this.name = '';
    this.email = '';
    this.password = '';
    this.group_id = null;
  }
}

@Component({
  selector: 'app-user-page',
  templateUrl: './user-page.component.html',
  styleUrls: ['./user-page.component.scss'],
  preserveWhitespaces: false
})
export class UserPageComponent implements OnInit, OnDestroy {
  public AuthorityUser: typeof AuthorityUser = AuthorityUser;
  @ViewChild(DatatableComponent) userTable: DatatableComponent;
  displayedUsers: UserSetting[] = [];
  originalUsersList: UserSetting[] = [];
  isLoading = false;
  searchKeyword: string;
  lengthMenu = [
    {value: 15, text: '15'},
    {value: 50, text: '50'},
    {value: 100, text: '100'},
    {value: -1, text: 'All'},
  ];
  pageSize = 15;
  userSubscription: Subscription;
  userSession: UserSetting;

  constructor(private userService: UserService,
              private dialog: DialogService,
              private notify: NotifierService,
              private shared: SharedValueService,
              private helper: Helper) { }

  ngOnInit() {
    setTimeout(() => {
      this.isLoading = true;
    });
    this.userSession = this.shared.getUserSession();
    this.userSubscription = this.userService.getAllUsers().subscribe(data => {
      for (let i = 0; i < data.length; i++) {
        data[i] = new UserSetting(data[i]);
      }
      this.originalUsersList = data;
      this.displayedUsers = [...this.originalUsersList];
      setTimeout(() => {
        this.isLoading = false;
      });
    }, error => {
      setTimeout(() => {
        this.isLoading = false;
      });
      this.notify.notify('error', 'Error occurred!');
    });
  }

  ngOnDestroy() {
    this.helper._unsubsribe(this.userSubscription);
  }

  showUserItems(length) {
    const selectedPageSize = parseInt(length, 10);
    if (selectedPageSize !== -1) {
      this.pageSize = selectedPageSize;
    } else {
      this.pageSize = this.displayedUsers.length;
    }
  }

  updateUserFilter(keyword) {
    this.isLoading = true;
    this.searchKeyword = keyword.trim().toLowerCase();
    if (this.searchKeyword !== '') {
      const tempUser = [...this.originalUsersList];
      // filter our data & update the rows
      this.displayedUsers = this.filterUserData(tempUser);
    } else {
      this.displayedUsers = [...this.originalUsersList];
    }
    this.isLoading = false;
    // Whenever the filter changes, always go back to the first page
    this.userTable.offset = 0;
  }

  // filter data
  filterUserData(tempUser: UserSetting[]) {
    return tempUser.filter((d) => {
      return d.name.toLowerCase().indexOf(this.searchKeyword) !== -1 || d.username.toLowerCase().indexOf(this.searchKeyword) !== -1 ||
        d.email.toLowerCase().indexOf(this.searchKeyword) !== -1 || !this.searchKeyword;
    });
  }

  getRowClass(row) {
    return {
      'bg-lightblue-1': row.isAdmin
    };
  }

  showModalAddUser() {
    const newUser = new User();
    const obj = {
      isAdd: true,
      model: newUser,
      isAPICallError: false,
      checkValidate: true,
    };
    this.dialog.showUserModal('Add User', 'Add',  'Close', obj)
      .subscribe(res => {
        if (res.isOk()) {
          const model = Object.assign(new User(), res.payload.model);
          model.name = model.name.trim();
          this.userService.postAddUser(model).subscribe(result => {
            this.notify.notify('success', result.message);
            this.shared.httpStatus.next(true);
            this.originalUsersList.push(result.data);
            const tempUser = [...this.originalUsersList];
            if (this.searchKeyword !== '') {
              this.displayedUsers = this.filterUserData(tempUser);
            } else {
              this.displayedUsers = [...this.originalUsersList];
            }
          }, error => {
            this.shared.httpStatus.next(false);
            const message = error.error.message ? error.error.message : 'Error occurred!';
            this.notify.notify('error', message);
          });
        }
      });
  }

  showModalViewUser(user) {
    if (user && user.isAdmin) {
      return;
    } else {
      if (user) {
        const obj = {
          isRestore: true,
          model: user,
          isAPICallError: false,
          checkValidate: true,
        };
        this.dialog.showUserModal('User Restore', 'Restore',  'Close', obj)
          .subscribe(res => {
            if (res.isOk()) {
              const model = {
                new_password: res.payload.model.password,
                confirm_password: res.payload.model.confirm_password
              };
              this.userService.restoreUser(user.id, model).subscribe(result => {
                this.notify.notify('success', result.message);
                this.shared.httpStatus.next(true);
              }, error => {
                this.shared.httpStatus.next(false);
                const message = error.error.message ? error.error.message : 'Error occurred!';
                this.notify.notify('error', message);
              });
            }
          });
      }
    }
  }

  showModalUpdateUser(user: any) {
    if (user && user.isAdmin) {
      return;
    } else {
      if (user) {
        const obj = {
          isEdit: true,
          model: user,
          isAPICallError: false,
          checkValidate: true,
        };
        this.dialog.showUserModal('Edit user', 'Save',  'Close', obj)
          .subscribe(res => {
            if (res.isOk()) {
              const model = Object.assign(new User(), res.payload.model);
              this.userService.updateUser(user.id, model).subscribe(result => {
                this.notify.notify('success', result.message);
                this.shared.httpStatus.next(true);
                const index = this.originalUsersList.findIndex(item => item.id === user.id);
                this.originalUsersList[index] = result.data;
                if (this.searchKeyword !== '') {
                  this.displayedUsers = this.filterUserData([...this.originalUsersList]);
                } else {
                  this.displayedUsers = [...this.originalUsersList];
                }
              }, error => {
                this.shared.httpStatus.next(false);
                const message = error.error.message ? error.error.message : 'Error occurred!';
                this.notify.notify('error', message);
              });
            }
          });
      }
    }
  }

  showModalDeleteUser(user: any) {
    if (user && user.isAdmin) {
      return;
    } else {
      if (user) {
        this.dialog.showMessage('Delete user', 'Are you sure delete this user?', null,
          'OK', null, null, 'Cancel').subscribe((res: DialogResult) => {
          if (res.isOk()) {
            this.userService.deleteUser(user.id).subscribe((result) => {
              this.notify.notify('success', result.message);
              this.originalUsersList = this.originalUsersList.filter(item => item.id !== parseInt(result.data, 10));
              if (this.searchKeyword !== '') {
                this.displayedUsers = this.filterUserData([...this.originalUsersList]);
              } else {
                this.displayedUsers = [...this.originalUsersList];
              }
            }, error => {
              const message = error.error.message ? error.error.message : 'Error occurred!';
              this.notify.notify('error', message);
            });
          }
        });
      }
    }
  }

  setUserRole(event: any, user: any) {
    if (user.isAdmin) {
      event.stopPropagation();
      return;
    } else {
      this.userService.setRole(user.id).subscribe(res => {
        this.notify.notify('success', res.message);

        const originalIndex = this.originalUsersList.findIndex(item => item.id === user.id);
        this.originalUsersList[originalIndex].isOwner = !this.originalUsersList[originalIndex].isOwner;

        const displayedIndex = this.displayedUsers.findIndex(item => item.id === user.id);
        this.displayedUsers[displayedIndex].isOwner = this.originalUsersList[originalIndex].isOwner;
      }, error => {
        const message = error.error.message ? error.error.message : 'Error occurred!';
        this.notify.notify('error', message);
      });
    }
  }

  setUserAuthority(user: any) {
    const model = {
      userId: user.id,
    };
    this.userService.setAuthority(model).subscribe(
      (res) => {
        this.notify.notify('success', res.message);
        this.changeAuthority(true, user);
      },
      (error) => {
        const message = error.error.message ? error.error.message : 'Error occurred!';
        this.notify.notify('error', message);
      }
    );
  }

  removeAuthority(user: any) {
    const model = {
      userId: user.id,
    };
    this.userService.removeAuthority(model).subscribe(
      (res) => {
        this.notify.notify('success', res.message);
        this.changeAuthority(false, user);
      },
      (error) => {
        const message = error.error.message ? error.error.message : 'Error occurred!';
        this.notify.notify('error', message);
      }
    );
  }

  changeAuthority(authority: boolean, user: any) {
    const originalIndex = this.originalUsersList.findIndex(item => item.id === user.id);
    this.originalUsersList[originalIndex].authority.AU_1 = authority;

    const displayedIndex = this.displayedUsers.findIndex(item => item.id === user.id);
    this.displayedUsers[displayedIndex].authority.AU_1 = authority;

    if (this.userSession && this.userSession.id === user.id) {
      this.shared.fetchUserSession().subscribe();
    }
  }

  refreshUserTable() {
    this.searchKeyword = '';
    setTimeout(() => {
      this.isLoading = true;
    });
    this.userService.getAllUsers().subscribe(users => {
      setTimeout(() => {
        this.isLoading = false;
      });
      for (let i = 0; i < users.length; i++) {
        users[i] = new UserSetting(users[i]);
      }
      this.originalUsersList = users;
      this.displayedUsers = [...this.originalUsersList];
    }, error => {
      setTimeout(() => {
        this.isLoading = false;
      });
    });
  }
}



// WEBPACK FOOTER //
// /app/src/app/pages/user-page/user-page.component.ts